import { BaseLayer } from '.'
import * as Cesium from 'cesium'
import type { Feature, FeatureCollection, Point } from 'geojson'
import {
  Cartesian2,
  Cartesian3,
  Color,
  DistanceDisplayCondition,
  HorizontalOrigin,
  LabelStyle,
  Entity,
  LabelGraphics,
  NearFarScalar,
  BillboardGraphics,
  Cartographic,
} from 'cesium'
import { Subscriber } from 'cesium-extends'
import { nanoid } from 'nanoid'
import { getAllShipDataInfo, type IshipAllInfo } from '@/apiFzn'
import ShipPopup from '../../views/Map/Popup/ShipPopup.vue' // 新增导入
import { createShipIconUrl, imgColorMap } from '@/assets/createShipIcon'

export class Ship66Layer extends BaseLayer<{
  click(p: IshipAllInfo): void
}> {
  layername: string = '船只'
  key: string = 'ship66'
  lasttime?: Date
  private updateInterval: number = 4000 // 更新间隔时间

  private readonly MIN_UPDATE_DISTANCE = 5 // 最小更新距离（米）

  constructor() {
    super()
    this.once('mounted', (viewer) => {
      this._subscriber = new Subscriber(viewer, { pickResult: { enable: true } })
      this._subscriber.addExternal((_e, result) => {
        const entity = result?.id
        if (!(entity instanceof Entity) || !this.datasource.entities.contains(entity)) return
        const data = entity.properties?.getValue()
        if (data) {
          this.emit('click', data)

          // 使用 LayerPopupCtrl 显示弹窗
          const position = entity.position?.getValue()
          if (!position) return
          const cartographic = Cartographic.fromCartesian(position)

          // 防止单个船只弹窗重复创建
          // 这里使用船只的 MMSI 或 Entity ID 作为唯一标识
          const popupId = data.mmsi || entity.id // 使用船只的 MMSI 或 Entity ID 作为唯一标识
          this.popupCtrl.closeAll()

          this.popupCtrl.popup({
            viewer: this._viewer!,
            id: popupId,
            lnglat: [
              Cesium.Math.toDegrees(cartographic.longitude),
              Cesium.Math.toDegrees(cartographic.latitude),
            ] as [number, number],
            Com: ShipPopup as any,
            properties: data,
          })
        }
      }, 'LEFT_CLICK')
    })
  }

  private async loadGeojson(): Promise<FeatureCollection<Point, Omit<IshipAllInfo, 'geom'>>> {
    // const res = await apiFzu.ships.shipsControllerGetShips()
    const res = await getAllShipDataInfo()
    const featureCollection: FeatureCollection<Point, Omit<IshipAllInfo, 'geom'>> = {
      type: 'FeatureCollection',
      features: [],
    }
    // let lastTime = 0
    res.rows.forEach((el) => {
      const { geometry, properties } = el
      // const { geom, ...properties } = el

      geometry.coordinates = geometry.coordinates[0] as any
      const feature: Feature<Point, IshipAllInfo> = {
        type: 'Feature',
        id: properties.mmsi ?? nanoid(),
        geometry: geometry,
        properties: properties,
      }
      featureCollection.features.push(feature)
      // lastTime = Math.max(new Date(properties.time).valueOf(), lastTime)
    })

    // this.lasttime = new Date(lastTime)
    return featureCollection
  }
  private entityMap = new Map<string, Entity>()
  async render() {
    const geojson = await this.loadGeojson()
    geojson.features.forEach((point) => {
      const simpled = new Cesium.SampledPositionProperty()

      const entity = new Entity({
        id: String(point.id),
        position: simpled,
        properties: point.properties,
      })
      this.datasource.entities.add(entity)
      this.entityMap.set(String(point.id), entity)
      this.renderEntity(entity)
      simpled.addSample(
        this._viewer?.clock.currentTime ?? Cesium.JulianDate.now(),
        Cartesian3.fromDegrees(point.geometry.coordinates[0], point.geometry.coordinates[1])
      )
      simpled.addSample(
        Cesium.JulianDate.fromIso8601('2100-01-01T00:00:00Z'), // 起始时间
        Cartesian3.fromDegrees(point.geometry.coordinates[0], point.geometry.coordinates[1])
      )
    })
  }

  private isUpdating = false

  private startUpdate() {
    if (this.isUpdating) return
    this.isUpdating = true
    this.update()
  }
  private stopUpdate() {
    this.isUpdating = false
    clearTimeout(this.updateHandler)
  }

  private showTimeout?: number
  show() {
    super.show()
    clearTimeout(this.showTimeout)
    this.showTimeout = setTimeout(() => {
      this.startUpdate()
    }, this.updateInterval)
  }

  hide(): void {
    super.hide()
    this.stopUpdate()
  }

  private updateHandler?: number
  /**
   * 更新动态
   */
  private async update() {
    clearTimeout(this.updateHandler)

    const geojson = await this.loadGeojson()
    // 处理更新
    geojson.features = geojson.features.filter((f) => {
      const entity = this.entityMap.get(String(f.id))
      if (!entity) return true

      const coordinates = f.geometry.coordinates

      // // #TODO 调试使用
      // coordinates[0] = coordinates[0] + Math.random() * 0.001
      // coordinates[1] = coordinates[1] + Math.random() * 0.001

      const [lng, lat] = coordinates

      const now = this._viewer?.clock.currentTime ?? Cesium.JulianDate.now()
      const currentPosition = entity.position!.getValue(now)!
      const futureTime = Cesium.JulianDate.addSeconds(now, 3, new Cesium.JulianDate())
      const newPos = Cartesian3.fromDegrees(lng, lat)
      const sampledPosition = entity.position
      // 使用 SampledPositionProperty 实现平滑过渡
      if (sampledPosition instanceof Cesium.SampledPositionProperty) {
        // @ts-ignore
        entity.position._property._values.pop()
        // @ts-ignore
        entity.position._property._values.pop()
        // @ts-ignore
        entity.position._property._values.pop()
        // @ts-ignore
        entity.position._property._times.pop()
        sampledPosition.addSample(
          Cesium.JulianDate.addSeconds(now, 1, new Cesium.JulianDate()),
          currentPosition
        ) // 目标位置
        sampledPosition.addSample(futureTime, newPos) // 目标位置
        sampledPosition.addSample(Cesium.JulianDate.fromIso8601('2100-01-01T00:00:00Z'), newPos) // 目标位置
      } else {
        const p = new Cesium.SampledPositionProperty()
        // 当前位置
        entity.position = p
        p.addSample(now, newPos)
        p.addSample(futureTime, newPos)
      }

      //  #TODO 船只从静止到移动，需要修改图标
      if (entity.properties) {
        for (const [key, value] of Object.entries(f.properties)) {
          entity.properties[key] = value
        }
      }
      return false // 过滤掉已更新的实体
    })
    // 批量处理新实体
    if (geojson.features.length > 0) {
      geojson.features.forEach((point) => {
        const simpled = new Cesium.SampledPositionProperty()
        const entity = new Entity({
          id: String(point.id),
          position: simpled,
          properties: point.properties,
        })
        this.datasource.entities.add(entity)
        this.entityMap.set(String(point.id), entity)
        this.renderEntity(entity)
        simpled.addSample(
          this._viewer?.clock.currentTime ?? Cesium.JulianDate.now(),
          Cartesian3.fromDegrees(point.geometry.coordinates[0], point.geometry.coordinates[1])
        )
        simpled.addSample(
          Cesium.JulianDate.fromIso8601('2100-01-01T00:00:00Z'), // 起始时间
          Cartesian3.fromDegrees(point.geometry.coordinates[0], point.geometry.coordinates[1])
        )
      })
    }
    this.updateHandler = setTimeout(() => {
      if (this.isUpdating) this.update()
    }, this.updateInterval)
  }

  /**entity样式渲染 */
  private renderEntity(entity: Entity) {
    const properties = entity.properties?.getValue()
    const dataType = (properties?.dataType as keyof typeof imgColorMap) ?? 'default'
    const imgColor = imgColorMap[dataType] ?? imgColorMap.default
    const speed = Number(properties?.speed ?? 0)
    const image = createShipIconUrl(imgColor, speed > 0)

    // 优化 billboard 显示
    entity.billboard = new BillboardGraphics({
      image,
      width: 20,
      height: 120,
      disableDepthTestDistance: 3000000,
      distanceDisplayCondition: new DistanceDisplayCondition(0, 10000),
      pixelOffset: new Cartesian2(0, 0),
      alignedAxis: Cartesian3.UNIT_Z,
      rotation: new Cesium.CallbackProperty(
        () =>
          entity.properties?.getValue()?.course
            ? (-1 * (entity.properties.getValue().course * Math.PI)) / 180
            : 0,
        false
      ), // 根据航向设置旋转角度
    })

    // 优化标签显示
    entity.label = new LabelGraphics({
      font: `12px "微软雅黑"`,
      showBackground: true,
      style: LabelStyle.FILL,
      backgroundColor: Color.fromCssColorString('#5582df'),
      pixelOffset: new Cartesian2(0, -20),
      scale: 1,
      scaleByDistance: new NearFarScalar(3e3, 1, 1e4, 0.55),
      text:
        entity.properties?.getValue()?.name ??
        (entity.properties?.getValue().id ? 'mmsi:' + entity.properties?.getValue().mmsi : '未知'),
      horizontalOrigin: HorizontalOrigin.CENTER,
      distanceDisplayCondition: new DistanceDisplayCondition(0, 10000),
      disableDepthTestDistance: 5e3,
    })
  }
}
